import { Type } from 'apache-arrow';
import { Observable } from '@reactivex/rxjs';
import { AsyncIterable } from 'ix/Ix';
import 'ix/add/asynciterable-operators/toobservable';
import { isBufServerSide } from '@graphistry/viz/src/models/scene';

export function asyncIterableToObservable(fn) {
    return (...xs) => asyncIteratorToObservable(fn(...xs));
}

export function asyncIteratorToObservable(x) {
    return Observable.from(AsyncIterable.as(x).toObservable());
}

export function getBufferNFOs({ table, batch = table, offset, nfos }, partial = true, tableName) {
    //tick-worker path: the columns were already extracted (and transferred)
    //off-thread, so the NFOs arrive prebuilt (getWorkerBatchNFOs)
    if (nfos) {
        return nfos;
    }
    const fields = batch.schema.fields;
    const bufferNFOs = Object.create(null);
    for (let i = -1, n = fields.length; ++i < n;) {
        const field = fields[i];
        const child = table ? table.getColumnAt(i) : batch.getChildAt(i);
        const values = getColumnValues(field, child.toArray());
        const byteOffset = getColumnByteWidth(field.type) * offset;
        bufferNFOs[getColumnName(tableName, field.name)] = !partial ? values : {
            data: values,
            partial, byteOffset,
            byteLength: values.byteLength,
        };
    }
    return bufferNFOs;
}

/*
 * Buffer NFOs from the tick worker's per-batch column transfers: same shape
 * getBufferNFOs builds from an Arrow batch, but over raw transferred
 * ArrayBuffers. rowBytes (bytes per table row for the column) positions the
 * slice in the whole-table buffer, exactly like getColumnByteWidth * offset.
 */
export function getWorkerBatchNFOs(tableName, columns, offset) {
    const bufferNFOs = Object.create(null);
    for (let i = -1, n = columns.length; ++i < n;) {
        const { name, buf, rowBytes } = columns[i];
        const values = getColumnValues({ name }, new Uint8Array(buf));
        bufferNFOs[getColumnName(tableName, name)] = {
            data: values,
            partial: true,
            byteOffset: rowBytes * offset,
            byteLength: values.byteLength,
        };
    }
    return bufferNFOs;
}

export function getNumElements(scene, tableName, tableLength) {

    const numElements = [];
    const { items: renderItems, models: rendererModels } = scene;
    const activePrograms = scene.render.reduce((xs, p) => (xs[p] = true) && xs, Object.create(null));
    const counts = tableName === 'node' ? getNodeTableCounts(tableLength) : getEdgeTableCounts(tableLength);

    if (tableName === 'edge') {
        numElements.push(['midedgeculled', tableLength]);
    }

    let itemName, item, binding, modelName, model;

    for ([itemName, item] of Object.entries(renderItems)) {
        if (!(activePrograms[itemName] || activePrograms[item.program])) {
            continue;
        }
        for (binding of Object.values(item.bindings)) {
            modelName = binding[0];
            model = rendererModels[modelName];
            if ((modelName in counts) && isBufServerSide(model)) {
                numElements.push([itemName, counts[modelName] || 0]);
                break;
            }
        }
    }

    return numElements;
}

function getNodeTableCounts(tableLength) {
    return {
        curPoints: tableLength,
        pointSizes: tableLength,
        pointColors: tableLength,
        forwardsEdgeStartEndIdxs: tableLength
    };
}

function getEdgeTableCounts(tableLength) {
    return {
        edgeColors: tableLength,
        logicalEdges: tableLength,
        midSpringsPos: tableLength,
        midEdgesColors: tableLength,
        forwardsEdgeToUnsortedEdge: tableLength
    };
}

function getColumnByteWidth(type) {
    switch (type.typeId) {
        case Type.FixedSizeBinary: return type.byteWidth;
        case Type.Int: return type.ArrayType.BYTES_PER_ELEMENT;
        case Type.Float: return type.ArrayType.BYTES_PER_ELEMENT;
        case Type.FloatingPoint: return type.ArrayType.BYTES_PER_ELEMENT;
        default: throw new Error(`Unexpected column type ${Type[type.typeId]}`);
    }
}

function getColumnName(tableName, columnName) {
    switch (columnName) {
        case 'size': return 'pointSizes';
        case 'color': return tableName === 'node' ? 'pointColors' : 'edgeColors';
        case 'edge': return 'logicalEdges';
        case 'position': return 'curPoints';
        case 'pointPositions': return 'curPoints';
        case 'forwards_edge_id': return 'forwardsEdgeToUnsortedEdge';
        case 'forwardsEdgeIDs': return 'forwardsEdgeToUnsortedEdge';
        case 'fwds_edge_offset': return 'forwardsEdgeStartEndIdxs';
        case 'forwardsEdgeOffsets': return 'forwardsEdgeStartEndIdxs';
        default: return columnName;
    }
}

function getColumnValues(field, arr) {
    let Type;
    switch (field.name) {
        case 'size': Type = Uint8Array; break;
        case 'pointSizes': Type = Uint8Array; break;
        case 'color': Type = Uint8Array; break;
        case 'edgeColors': Type = Uint8Array; break;
        case 'pointColors': Type = Uint8Array; break;
        case 'edge': Type = Uint32Array; break;
        case 'logicalEdges': Type = Uint32Array; break;
        case 'forwards_edge_id': Type = Uint32Array; break;
        case 'forwardsEdgeIDs': Type = Uint32Array; break;
        case 'position': Type = Float32Array; break;
        case 'pointPositions': Type = Float32Array; break;
        case 'fwds_edge_offset': Type = Uint32Array; break;
        case 'forwardsEdgeOffsets': Type = Uint32Array; break;
        default: Type = arr.constructor; break;
    }
    return new Type(arr.buffer, arr.byteOffset, arr.byteLength / Type.BYTES_PER_ELEMENT);
}
